import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowRight, Quote } from "lucide-react";

export const Route = createFileRoute("/case-study")({
  head: () => ({
    meta: [
      { title: "PHMG Case Study — SaaSed" },
      { name: "description", content: "How PHMG eliminated a proposed renewal uplift and rebuilt a fair, future-proof Salesforce contract with SaaSed." },
      { property: "og:title", content: "PHMG Case Study — SaaSed" },
      { property: "og:description", content: "How PHMG eliminated a proposed renewal uplift and rebuilt a fair, future-proof Salesforce contract." },
      { property: "og:url", content: "https://saased.co/case-study" },
    ],
    links: [{ rel: "canonical", href: "https://saased.co/case-study" }],
  }),
  component: CaseStudyPage,
});

function CaseStudyPage() {
  return (
    <>
      <section className="container-x py-20 md:py-28">
        <div className="mb-12 flex flex-col items-start justify-between gap-4 md:flex-row md:items-end">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">
              Featured case study
            </p>
            <h1 className="mt-3 text-4xl md:text-5xl">PHMG × SaaSed</h1>
          </div>
          <p className="max-w-md text-sm text-muted-foreground">
            How PHMG eliminated a proposed renewal uplift and rebuilt a fair, future-proof Salesforce contract.
          </p>
        </div>

        <div className="grid gap-10 rounded-3xl border border-border bg-card p-8 shadow-soft md:grid-cols-2 md:p-12">
          {/* Left: narrative + quote */}
          <div className="flex flex-col gap-10 md:gap-12">
            <div className="flex flex-col gap-4">
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">
                The challenge
              </p>
              <p className="text-lg leading-relaxed text-foreground text-justify font-sans">
                Years of platform drift, governance gaps, and a proposed renewal uplift that no longer
                reflected actual usage or business value.
              </p>
            </div>
            <div className="flex flex-col gap-4">
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">
                The approach
              </p>
              <p className="text-lg leading-relaxed text-foreground text-justify font-sans">
                An independent commercial review backed by £1B+ in analysed renewal benchmarks —
                discovery, market positioning, and direct negotiation with Salesforce.
              </p>
            </div>

            <figure className="rounded-2xl border border-border bg-secondary/40 p-6">
              <Quote size={20} className="text-primary" />
              <blockquote className="text-muted-foreground font-serif">
                "SaaSed helped bring structure and clarity to a renewal that had grown increasingly complex.
                The process was disciplined, data-driven, and delivered a stronger long-term commercial
                position than we expected."
              </blockquote>
              <figcaption className="mt-4 text-sm">
                <span className="font-medium">Matthew Toynton</span>
                <span className="text-muted-foreground"> · CFO, PHMG</span>
              </figcaption>
            </figure>
          </div>

          {/* Right: results */}
          <div className="flex flex-col gap-5">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">
              The outcome
            </p>
            {[
              { stat: "19%", label: "Total cost reduction while growing 18% in sales capacity" },
              { stat: "Removed", label: "Legacy add-ons and unused licenses stripped out" },
              { stat: "Expanded", label: "Sales & Service Cloud capacity on fair terms" },
              { stat: "Lower baseline", label: "Materially reduced long-term cost foundation" },
            ].map((r) => (
              <div
                key={r.label}
                className="flex items-baseline gap-5 rounded-2xl border border-border bg-background p-6"
              >
                <p className="font-serif text-3xl text-foreground md:text-4xl">{r.stat}</p>
                <p className="text-sm text-muted-foreground">{r.label}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-12 text-center">
          <a
            href="https://saased.co/blog/inside-phmgs-salesforce-renewal-a-conversation-with-cfo-matthew-toynton?page=1"
            className="mb-4 inline-flex items-center gap-2 font-sans text-sm font-medium uppercase tracking-[0.15em] text-foreground underline underline-offset-4 transition-colors hover:text-primary"
          >
            Read more: Inside PHMG's Salesforce Renewal: A Conversation with CFO Matthew Toynton
            <ArrowRight size={16} />
          </a>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">
            Podcast
          </p>
          <h2 className="mt-5 text-3xl md:text-4xl">Watch the Discussion</h2>
          <div className="mt-8 overflow-hidden rounded-3xl border border-border bg-card shadow-soft">
            <div className="aspect-video w-full">
              <iframe
                className="h-full w-full"
                src="https://fast.wistia.net/embed/iframe/xgy6otd34z"
                title="PHMG × SaaSed Podcast"
                allow="autoplay; fullscreen"
                allowFullScreen
                loading="lazy"
              />
            </div>
          </div>
        </div>

        <div className="mt-12 flex flex-col items-center gap-4 text-center">
          <p className="text-base text-muted-foreground">
            Want the same clarity on your next Salesforce renewal?
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center gap-2 rounded-full bg-primary px-7 py-3 text-primary-foreground shadow-glow transition-transform hover:-translate-y-0.5 font-sans text-sm font-medium uppercase tracking-[0.15em]"
          >
            Contact Us <ArrowRight size={18} />
          </Link>
        </div>
      </section>
    </>
  );
}
